/**
 * @file PageDiffList.tsx
 * @description Список «Что изменено» страницы (phase19.16 Р3а; uiux-r3 §3.1,
 * §3.2 — «›» раскрывает правки по блокам): значок и текст, не только цвет;
 * больше `limit` строк — «и ещё N».
 */

import type { SitePageDiffItem } from "@chaiberi/shared";
import { Minus, Pencil, Plus, type LucideIcon } from "lucide-react";

import { diffLines, type DiffLineTone } from "../../../lib/site-builder/page-diff-lines";
import { cn } from "@/lib/utils";

/** Значок и цвет строки (реестр — новый вид изменения записью). */
const TONE_META: Readonly<Record<DiffLineTone, { icon: LucideIcon; className: string; sr: string }>> = {
  added: { icon: Plus, className: "text-success", sr: "Добавлено" },
  changed: { icon: Pencil, className: "text-info", sr: "Изменено" },
  removed: { icon: Minus, className: "text-destructive", sr: "Удалено" },
};

interface Props {
  items: readonly SitePageDiffItem[];
  limit?: number;
  className?: string;
}

export function PageDiffList({ items, limit, className }: Props) {
  const lines = diffLines(items);
  if (lines.length === 0) return <p className={cn("type-caption text-on-surface-variant", className)}>Отличий от сайта нет.</p>;
  const shown = limit ? lines.slice(0, limit) : lines;
  const rest = lines.length - shown.length;
  return (
    <ul className={cn("flex flex-col gap-1", className)}>
      {shown.map((line) => {
        const meta = TONE_META[line.tone];
        const Icon = meta.icon;
        return (
          <li key={line.key} className="type-caption flex min-w-0 items-start gap-2 text-on-surface">
            <Icon aria-hidden className={cn("mt-0.5 size-3.5 shrink-0", meta.className)} />
            <span className="sr-only">{meta.sr}: </span>
            <span className="min-w-0 break-words">{line.text}</span>
          </li>
        );
      })}
      {rest > 0 ? <li className="type-caption pl-5 text-on-surface-variant">и ещё {rest}</li> : null}
    </ul>
  );
}
